/* eslint-disable no-nested-ternary */
import { useRoutes, useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import shuffle from 'lodash/shuffle';
import PropTypes from 'prop-types';
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useLocation, useParams, useRoute } from 'wouter';

import { useAnswerQuestion } from '../../hooks/useGame';
import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';

import feedbacks from '../../../videos/feedbacks';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import SportGroupsPills from '../lists/SportGroupsPills';
import SportQuestionsList from '../lists/SportQuestions';
import GroupQuestionsAnsweredModal from '../modals/GroupQuestionsAnsweredModal';
import VideoModal from '../modals/VideoModal';
import Body from '../partials/Body';
import NhlQuestionPointsBanner from '../partials/NhlQuestionPointsBanner';
import QuestionStatus from '../partials/QuestionStatus';
import SportBanner from '../partials/SportBanner';
import SportQuestionsSelector from '../partials/SportQuestionsSelector';
import Question from '../questions/Question';

import styles from '../../../styles/games/sport.module.scss';

const propTypes = {
    className: PropTypes.string,
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    canUpdateAnswer: PropTypes.bool,
    nextQuestionDelay: PropTypes.number,
    gameStatsComponent: PropTypes.elementType,
    gameStatsDetailedComponent: PropTypes.elementType,
    endedComponent: PropTypes.elementType,
    // reload: PropTypes.func,
};

const defaultProps = {
    className: null,
    game: null,
    participation: null,
    canUpdateAnswer: false,
    nextQuestionDelay: 250, // miliseconds
    gameStatsComponent: null,
    gameStatsDetailedComponent: null,
    endedComponent: null,
};

function SportGame({
    className,
    game,
    participation,
    canUpdateAnswer,
    nextQuestionDelay,
    gameStatsComponent,
    gameStatsDetailedComponent,
    endedComponent: EndedComponent,
}) {
    const { locale } = useIntl();
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const setUser = useSetUser();
    const [loading, setLoading] = useState(false);
    const [answeredModalQuestionsGroup, setAnsweredModalQuestionsGroup] = useState(null);
    const [answeredModalVideo, setAnsweredModalVideo] = useState(null);

    const video = useMemo(
        () =>
            feedbacks[locale] !== null && feedbacks[locale].length > 0
                ? shuffle(feedbacks[locale] || []).pop() || null
                : null,
        [locale],
    );

    const tracking = useTracking();
    const { name: theme = null, isVariant } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const { id: gameId, sportGame, questionsGroups, type = null } = game || {};
    const questions = useMemo(() =>
        questionsGroups.reduce(
            (all, { questions: groupQuestions = [] }) => [...all, ...groupQuestions],
            [],
        ),
    );

    const {
        id: participationId = null,
        endedAt: participationEndedAt = null,
        answers = [],
        points,
        prizes,
    } = participation || {};

    const ended =
        participationEndedAt !== null &&
        differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0;

    const { question: questionSlug = null } = useParams();

    const currentQuestion = useMemo(
        () => questions.find(({ slug }) => slug === questionSlug) || null,
        [questions, questionSlug],
    );

    const {
        id: currentQuestionId,
        body: currentQuestionBody,
        canAnswer: currentQuestionCanAnswer,
        expired: currentQuestionExpired,
        type: currentQuestionType,
    } = currentQuestion || {};
    const currentQuestionGroup =
        questionsGroups.find(
            ({ questions: groupQuestions = [] }) =>
                groupQuestions.findIndex(({ id }) => currentQuestionId === id) !== -1,
        ) || null;
    const { type: questionGroupType = null, label: currentGroupLabel = null } =
        currentQuestionGroup || {};

    const isCurrentGroupTrivia = questionGroupType === 'trivia';

    // for some obscure and unknown reason, questionSlug (from which showQuestion is indirectly derived from) keeps its value
    // even after navigating back to the questions list with the Back button. So the Back button doesn't work as expected.
    // This is a temporary workaround to fix this issue until we take the time to debug the issue (TODO).
    const {
        'game.question': gameQuestionRoute = null,
        'participation.question': participationQuestionRoute = null,
    } = useRoutes();
    const [isGameQuestionRoute] = useRoute(gameQuestionRoute);
    const [isParticipationQuestionRoute] = useRoute(participationQuestionRoute);

    const showQuestion =
        currentQuestion !== null && (isGameQuestionRoute || isParticipationQuestionRoute);
    const currentQuestionIndex = useMemo(
        () => (showQuestion ? questions.findIndex(({ id }) => id === currentQuestionId) : -1),
        [questions, showQuestion, currentQuestionId],
    );

    const showGames = !showQuestion;

    const questionsCount = questions.length;
    const { periods, teamAway, teamHome } = sportGame || {};
    const {
        name: teamAwayName = null,
        location: teamAwayLocation,
        abbreviation: teamAwayAbbreviation,
    } = teamAway || {};
    const {
        name: teamHomeName,
        location: teamHomeLocation,
        abbreviation: teamHomeAbbreviation,
    } = teamHome || {};

    const teamAwayLabel = type === 'nfl' || type === 'nhl' ? teamAwayLocation : teamAwayName;
    const finalTeamAwayLabel =
        teamAwayLabel !== null && teamAwayLabel.length > 0 ? teamAwayLabel : teamAwayAbbreviation;

    const teamHomeLabel = type === 'nfl' || type === 'nhl' ? teamHomeLocation : teamHomeName;
    const finalTeamHomeLabel =
        teamHomeLabel !== null && teamHomeLabel.length > 0 ? teamHomeLabel : teamHomeAbbreviation;

    const groupsRefs = useRef(
        questionsGroups.reduce(
            (map, { id }) => ({
                ...map,
                [id]: createRef(),
            }),
            {},
        ),
    );

    const currentAnswer = useMemo(
        () => answers.find(({ question }) => question === currentQuestionId) || null,
        [answers, currentQuestionId],
    );

    const { validated: currentAnswerValidated } = currentAnswer || {};
    const { length: answersCount = 0 } = answers || [];
    const hasAnswer = currentAnswer !== null;
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const answerSelected = selectedAnswer !== null;
    const { question: selectedAnswerQuestionId } = selectedAnswer || {};

    const finalSelectedAnswer =
        selectedAnswerQuestionId === currentQuestionId ? selectedAnswer : currentAnswer;

    const isCurrentQuestionLocked =
        !ended &&
        !currentAnswerValidated &&
        !hasAnswer &&
        (!currentQuestionCanAnswer || currentQuestionExpired);

    const canAnswerQuestion =
        !ended &&
        (!hasAnswer || canUpdateAnswer) &&
        currentQuestionCanAnswer &&
        !currentQuestionExpired;

    const onBackToAllQuestionsClick = useCallback(() => {
        setAnsweredModalQuestionsGroup(null);
    }, []);

    const onClickQuestion = useCallback(() => {
        setAnsweredModalQuestionsGroup(null);
    }, []);

    const onSelectAnswer = useCallback(
        (answer) => {
            setSelectedAnswer({ question: currentQuestionId, value: answer });
        },
        [currentQuestionId],
    );

    const onGroupClick = useCallback(({ id }) => {
        const { top: periodTop } = groupsRefs.current[id].current.getBoundingClientRect();
        window.scrollTo({ top: periodTop, behavior: 'smooth' });
    }, []);

    const gotoNextQuestion = useCallback(() => {
        const foundIndex = questions.findIndex(({ id }) => id === currentQuestionId);
        if (foundIndex !== -1 && foundIndex < questions.length - 1) {
            const nextQuestion = questions[foundIndex + 1];
            const { slug } = nextQuestion || {};
            navigate(
                participationId !== null
                    ? route('participation.question', {
                          participation: participationId,
                          question: slug,
                      })
                    : route('game.question', { game: gameId, question: slug }),
            );
        }
    }, [questions, currentQuestionId, questions]);

    const { answerQuestionAsync } = useAnswerQuestion({
        game,
        participation,
    });

    const onConfirmClick = useCallback(() => {
        const { value = null } = selectedAnswer || {};
        setLoading(true);
        answerQuestionAsync({
            questionId: currentQuestionId,
            answer: value,
        }).then(
            ({
                participation: updatedParticipation,
                participant: updatedParticipant,
                game: updatedGame = null,
            }) => {
                setTimeout(() => {
                    setSelectedAnswer(null);
                    setLoading(false);

                    const { answers: updatedAnswers } = updatedParticipation || {};
                    const {
                        id: currentQuestionGroupId,
                        questions: groupQuestions = [],
                        answeredModalTitle = null,
                        period: currentQuestionGroupPeriod = null,
                    } = currentQuestionGroup || {};

                    const isAllGroupQuestionsAnswered = (groupQuestions || []).reduce(
                        (allAnswered, { id, expired } = {}) => {
                            const answered =
                                updatedAnswers.findIndex(
                                    ({ question }) => question === id || null,
                                ) > -1;
                            return allAnswered && (answered || expired);
                        },
                        true,
                    );

                    const groupHasValidAnswer = (groupQuestions || []).reduce(
                        (hasValidOne, { valid = null } = {}) => {
                            if (valid === true) {
                                return true;
                            }
                            return hasValidOne;
                        },
                        false,
                    );

                    const currentQuestionAnswer =
                        updatedAnswers.find(({ question }) => question === currentQuestionId) ||
                        null;
                    const {
                        validated: currentQuestionAnswerValidated,
                        valid: currentAnswerValid = false,
                    } = currentQuestionAnswer || {};

                    if (
                        theme === 'nfl' &&
                        video !== null &&
                        currentQuestionAnswerValidated &&
                        currentAnswerValid &&
                        !groupHasValidAnswer
                    ) {
                        setAnsweredModalVideo(true);
                    }

                    if (currentQuestionAnswerValidated) {
                        // do nothing, just shows the right answer
                    } else if (
                        !hasAnswer &&
                        isAllGroupQuestionsAnswered &&
                        answeredModalTitle !== null &&
                        (theme !== 'nfl' ||
                            currentQuestionGroupPeriod === 2 ||
                            currentQuestionGroupPeriod === 4)
                    ) {
                        setAnsweredModalQuestionsGroup(currentQuestionGroupId);
                    } else {
                        gotoNextQuestion();
                    }
                }, nextQuestionDelay);

                tracking.trackInteraction('game_answer_question', {
                    game: updatedGame || game,
                    participant: updatedParticipant,
                    participation: updatedParticipation,
                    question: currentQuestion,
                    choice: value,
                });
            },
        );
    }, [
        theme,
        selectedAnswer,
        currentQuestionId,
        currentQuestionGroup,
        gameId,
        periods,
        questions,
        hasAnswer,
        setUser,
        gotoNextQuestion,
        nextQuestionDelay,
        setAnsweredModalQuestionsGroup,
        setAnsweredModalVideo,
        video,
    ]);

    const onGroupQuestionsAnsweredModalClose = useCallback(() => {
        gotoNextQuestion();
        setAnsweredModalQuestionsGroup(null);
    }, [gotoNextQuestion, setAnsweredModalQuestionsGroup]);

    useEffect(() => {
        setSelectedAnswer(null);
    }, [currentQuestionId]);

    const prevQuestionIndexRef = useRef(null);
    const isQuestionTransitionReversed = currentQuestionIndex < prevQuestionIndexRef.current;

    useEffect(() => {
        window.scroll({
            top: 0,
            behavior: 'smooth',
        });

        if (showQuestion) {
            prevQuestionIndexRef.current = currentQuestionIndex;
        }
    }, [showQuestion]);

    const currentRoute =
        participationId !== null
            ? route('participation', { participation: participationId })
            : route('game', { game: gameId });

    const nextQuestionSlug = useMemo(() => {
        const nextQuestion =
            currentQuestionIndex < questions.length - 1
                ? questions[currentQuestionIndex + 1]
                : null;
        if (nextQuestion === null) {
            return null;
        }
        const { slug } = nextQuestion || {};
        return participationId !== null
            ? route('participation.question', {
                  participation: participationId,
                  question: slug,
              })
            : route('game.question', {
                  game: gameId,
                  question: slug,
              });
    }, [questions, currentQuestionIndex, participationId, gameId]);

    // React-Transition-Group
    const rtgNodes = useRef({});
    if ((rtgNodes.current[currentQuestionId] || null) === null) {
        rtgNodes.current[currentQuestionId] = createRef();
    }
    const rtgClassNames = useMemo(
        () => ({
            enterActive: styles.enterActive,
            enterDone: styles.enterDone,
            exitActive: styles.exitActive,
            exitDone: styles.exitDone,
        }),
        [],
    );

    const onVideoClose = useCallback(() => {
        setAnsweredModalVideo(false);
    }, [video]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.showQuestion]: showQuestion,
                    [styles.ended]: ended,
                    [styles.loading]: loading,
                    [styles.isQuestionTransitionReversed]: isQuestionTransitionReversed,
                    [styles.isCurrentQuestionTrivia]: isCurrentGroupTrivia,
                },
                themeClassName,
                className,
            ])}
        >
            <SportBanner
                className={styles.backBanner}
                gameStatsComponent={gameStatsComponent}
                gameStatsDetailedComponent={gameStatsDetailedComponent}
                sportGame={sportGame}
                showBackLink={showQuestion || showGames}
                onBackClick={onBackToAllQuestionsClick}
                backHref={showQuestion ? currentRoute : route('play')}
                backLabel={
                    showQuestion ? (
                        <FormattedMessage
                            defaultMessage="Return to question selection"
                            description="button label"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Return to game selection"
                            description="button label"
                        />
                    )
                }
                label={
                    <>
                        <FormattedMessage defaultMessage="Answered:" description="banner label" />
                        <br />
                        {`${answersCount}/${questionsCount}`}
                    </>
                }
            />
            <div className={styles.questionsContainer}>
                {ended ? (
                    <EndedComponent
                        className={styles.endedContent}
                        points={points}
                        prizes={prizes}
                    />
                ) : (
                    <>
                        <SportGroupsPills
                            className={styles.periods}
                            items={questionsGroups.filter(
                                ({ type: groupType }) => groupType !== 'trivia',
                            )}
                            onGroupClick={onGroupClick}
                        />
                        <div className={styles.teams}>
                            <div className={styles.content}>
                                <h2 className={styles.label}>
                                    <span className={styles.teamAway}>
                                        <span className={styles.name}>{finalTeamAwayLabel}</span>
                                    </span>
                                    <span className={styles.vs}>
                                        <FormattedMessage
                                            defaultMessage="VS"
                                            description="NHL versus label"
                                        />
                                    </span>
                                    <span className={styles.teamHome}>
                                        <span className={styles.name}>{finalTeamHomeLabel}</span>
                                    </span>
                                </h2>
                            </div>
                        </div>
                        <div className={styles.ctaLabel}>
                            {type === 'nhl' ? (
                                isVariant('allstar') ? (
                                    <FormattedMessage
                                        defaultMessage="Predict game outcomes to increase your chances of winning"
                                        description="Sport game cta label"
                                    />
                                ) : isVariant('mvp') ? (
                                    <FormattedMessage
                                        defaultMessage="It’s game time!{br}Make the most valuable picks to score points and win big!"
                                        description="NHL game cta label"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="It’s game time!{br}Call the right shots to score points and win big!"
                                        description="NHL game cta label"
                                    />
                                )
                            ) : null}
                            {type === 'nfl' ? (
                                <FormattedMessage
                                    defaultMessage="Game on!{br}Predict the right plays to score points and win big!"
                                    description="NHL game cta label"
                                />
                            ) : null}
                        </div>
                    </>
                )}
                <SportQuestionsList
                    className={styles.questions}
                    groupsRefs={groupsRefs}
                    participation={participation}
                    game={game}
                    onItemClick={onClickQuestion}
                />
            </div>
            <div className={styles.currentQuestionContainer}>
                {theme === 'nhl' ? (
                    <NhlQuestionPointsBanner
                        className={styles.questionsPointsBanner}
                        question={currentQuestion}
                    />
                ) : null}
                <div className={styles.currentQuestionMain}>
                    {showQuestion ? (
                        <div className={styles.currentQuestionInner}>
                            <div className={styles.currentQuestionInfos}>
                                {isCurrentGroupTrivia ? (
                                    <div className={styles.triviaPill}>{currentGroupLabel}</div>
                                ) : (
                                    <div className={styles.period}>{currentGroupLabel}</div>
                                )}
                                {theme !== 'nfl' ? (
                                    <div className={styles.question}>
                                        <div className={styles.label}>
                                            <FormattedMessage
                                                defaultMessage="Question"
                                                description="NHL question index label"
                                            />
                                        </div>
                                        <div className={styles.progress}>{`${
                                            currentQuestionIndex + 1
                                        }/${questionsCount}`}</div>
                                    </div>
                                ) : null}
                                <QuestionStatus
                                    className={styles.status}
                                    ended={ended}
                                    question={currentQuestion}
                                    answer={currentAnswer}
                                    withPill
                                />
                            </div>
                            <TransitionGroup className={styles.currentQuestionGroup}>
                                <CSSTransition
                                    key={currentQuestionId}
                                    classNames={rtgClassNames}
                                    nodeRef={rtgNodes.current[currentQuestionId]}
                                    timeout={200}
                                    onEntered={() => {
                                        prevQuestionIndexRef.current = currentQuestionIndex;
                                    }}
                                >
                                    <div
                                        className={classNames([
                                            styles.currentQuestionTransition,
                                            {
                                                [styles.locked]: isCurrentQuestionLocked,
                                                [styles.expired]: currentQuestionExpired,
                                                [styles.answerSelected]: answerSelected,
                                                [styles.hasAnswer]: hasAnswer,
                                                [styles.canUpdateAnswer]: canUpdateAnswer,
                                                [styles.isTrivia]: currentQuestionType === 'trivia',
                                            },
                                        ])}
                                        ref={rtgNodes.current[currentQuestionId]}
                                    >
                                        <div className={styles.currentQuestionFrame}>
                                            <div className={styles.currentQuestionFrameInner}>
                                                <div className={styles.currentQuestionContent}>
                                                    <Body
                                                        tag="h2"
                                                        className={styles.currentQuestionTitle}
                                                        body={currentQuestionBody}
                                                    />
                                                    <Question
                                                        className={styles.currentQuestion}
                                                        question={currentQuestion}
                                                        answer={finalSelectedAnswer}
                                                        onAnswer={onSelectAnswer}
                                                        withoutBody
                                                    />
                                                </div>
                                                <div className={styles.currentQuestionLocked}>
                                                    <h3
                                                        className={
                                                            styles.currentQuestionLockedMessage
                                                        }
                                                    >
                                                        {currentQuestionExpired ? (
                                                            <FormattedMessage
                                                                defaultMessage="This question has expired.{br}Answer faster next time!"
                                                                description="NHL question blocker"
                                                            />
                                                        ) : (
                                                            <FormattedMessage
                                                                defaultMessage="This question is not available yet.{br}Come back soon!"
                                                                description="NHL question blocker"
                                                            />
                                                        )}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.confirmAnswerButtonContainer}>
                                            {canAnswerQuestion ? (
                                                <Button
                                                    className={styles.confirmAnswerButton}
                                                    onClick={onConfirmClick}
                                                >
                                                    <span className={styles.label}>
                                                        {canUpdateAnswer ? (
                                                            hasAnswer ? (
                                                                <FormattedMessage
                                                                    defaultMessage="Update my answer"
                                                                    description="Button label"
                                                                />
                                                            ) : (
                                                                <FormattedMessage
                                                                    defaultMessage="Save my answer"
                                                                    description="Button label"
                                                                />
                                                            )
                                                        ) : hasAnswer ? (
                                                            <FormattedMessage
                                                                defaultMessage="Confirmed"
                                                                description="Button label"
                                                            />
                                                        ) : (
                                                            <FormattedMessage
                                                                defaultMessage="Confirm and lock my answer"
                                                                description="Button label"
                                                            />
                                                        )}
                                                    </span>
                                                </Button>
                                            ) : (
                                                <Button
                                                    className={classNames([
                                                        styles.nextButton,
                                                        {
                                                            [styles.withoutLink]:
                                                                nextQuestionSlug === null,
                                                        },
                                                    ])}
                                                    href={nextQuestionSlug}
                                                >
                                                    <span className={styles.label}>
                                                        <FormattedMessage
                                                            defaultMessage="Next"
                                                            description="Button label"
                                                        />
                                                    </span>
                                                    <span className={styles.icon}>
                                                        <FontAwesomeIcon icon={faChevronRight} />
                                                    </span>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CSSTransition>
                            </TransitionGroup>
                        </div>
                    ) : null}
                    <SportQuestionsSelector
                        className={styles.questionsSelector}
                        currentQuestion={currentQuestion}
                        game={game}
                        participation={participation}
                        onClickQuestion={onClickQuestion}
                    />
                </div>
                {answeredModalQuestionsGroup !== null ? (
                    <GroupQuestionsAnsweredModal
                        questionsGroups={questionsGroups}
                        currentGroup={answeredModalQuestionsGroup}
                        onClose={onGroupQuestionsAnsweredModalClose}
                    />
                ) : null}
                {video !== null && answeredModalVideo ? (
                    <VideoModal
                        video={video}
                        closesOnEnded
                        allowCloseOnEnded={type === 'nfl'}
                        delay={type === 'nfl' ? 2000 : null}
                        onClose={onVideoClose}
                    />
                ) : null}
            </div>
        </div>
    );
}

SportGame.propTypes = propTypes;
SportGame.defaultProps = defaultProps;

export default SportGame;
